var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useRef, useState } from 'react';
const AUTO_UPDATE_INTERVAL = 1000;
function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
}
function useEventListener(eventName, handler, element) {
    const savedHandler = useRef(handler);
    useEffect(() => {
        savedHandler.current = handler;
    }, [handler]);
    useEffect(() => {
        if (!(element && element.addEventListener)) {
            return () => { };
        }
        const eventListener = (event) => {
            if (!!(savedHandler === null || savedHandler === void 0 ? void 0 : savedHandler.current)) {
                savedHandler.current(event);
            }
        };
        element.addEventListener(eventName, eventListener);
        return () => {
            element.removeEventListener(eventName, eventListener);
        };
    }, [eventName, element]);
}
function useKeyPress(targetKey, preventDefaultAction = false) {
    const [keyPressed, setKeyPressed] = useState(false);
    function downHandler(event) {
        if (event.key === targetKey) {
            setKeyPressed(true);
            if (preventDefaultAction) {
                event.preventDefault();
                event.stopPropagation();
            }
        }
    }
    const upHandler = (event) => {
        if (event.key === targetKey) {
            setKeyPressed(false);
            if (preventDefaultAction) {
                event.preventDefault();
                event.stopPropagation();
            }
        }
    };
    useEffect(() => {
        window.addEventListener('keydown', downHandler, false);
        window.addEventListener('keyup', upHandler, false);
        return () => {
            window.removeEventListener('keydown', downHandler);
            window.removeEventListener('keyup', upHandler);
        };
    }, []);
    return keyPressed;
}
function useAutoUpdate(updateIntervalTime = AUTO_UPDATE_INTERVAL) {
    const [tick, setTick] = useState(0);
    useEffect(() => {
        const interval = setInterval(() => {
            setTick((prevValue) => prevValue + 1);
        }, updateIntervalTime);
        return () => {
            clearInterval(interval);
        };
    }, []);
    return tick;
}
export const useCancellation = () => {
    const [isCancelled, setIsCancelled] = useState(false);
    useEffect(() => {
        return () => {
            setIsCancelled(true);
        };
    }, []);
    const executeIfNotCancelled = useCallback((block) => {
        if (block instanceof Function && !isCancelled) {
            block();
        }
    }, [isCancelled]);
    return { isCancelled, executeIfNotCancelled };
};
export const usePlayerTimes = (playerInstance) => {
    const { isCancelled } = useCancellation();
    const [elapsedTime, setElapsedTime] = useState(() => {
        const currentTime = playerInstance === null || playerInstance === void 0 ? void 0 : playerInstance.currentTime;
        return typeof currentTime === 'number' ? currentTime : 0;
    });
    const [durationTime, setDurationTime] = useState(() => {
        const duration = playerInstance === null || playerInstance === void 0 ? void 0 : playerInstance.duration;
        return typeof duration === 'number' ? duration : 1;
    });
    const [loadedMetadata, setLoadedMetadata] = useState(!!(playerInstance === null || playerInstance === void 0 ? void 0 : playerInstance.isMetadataLoaded()));
    const onDurationChange = useCallback(({ duration }) => {
        if (isCancelled || duration === durationTime)
            return;
        setDurationTime(duration);
    }, [setDurationTime, durationTime]);
    const onTimeUpdate = useCallback(({ currentTime }) => {
        if (isCancelled || currentTime === elapsedTime)
            return;
        setElapsedTime(currentTime);
    }, [setElapsedTime, elapsedTime]);
    const onLoadedMetadata = useCallback(() => {
        if (isCancelled || loadedMetadata)
            return;
        setLoadedMetadata(true);
    }, [setLoadedMetadata, loadedMetadata]);
    useEventListener('emptied', () => {
        onTimeUpdate({ currentTime: 0 });
        onDurationChange({ duration: 0 });
        setLoadedMetadata(false);
    }, playerInstance);
    useEventListener('timeupdate', onTimeUpdate, playerInstance);
    useEventListener('durationchange', onDurationChange, playerInstance);
    useEventListener('loadedmetadata', onLoadedMetadata, playerInstance);
    useEffect(() => {
        (() => __awaiter(void 0, void 0, void 0, function* () {
            if (playerInstance) {
                const duration = yield playerInstance.duration;
                if (isCancelled || duration === durationTime)
                    return;
                setDurationTime(duration);
            }
        }))();
    }, [playerInstance === null || playerInstance === void 0 ? void 0 : playerInstance.duration]);
    return { elapsedTime, durationTime, loadedMetadata };
};
export { usePrevious, useEventListener, useKeyPress, useAutoUpdate };
//# sourceMappingURL=common.js.map